import React from 'react'
import { UIButton, type UIButtonProps } from './ui-button'
import { cn } from '../../lib/utils'
import { UIBox } from '../layout/ui-box'

export interface UIButtonGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  orientation?: 'horizontal' | 'vertical'
  attached?: boolean
}

export interface UIButtonGroupItemProps extends UIButtonProps {
  /** Button variant for group items. @default "outline" */
  variant?: UIButtonProps['variant']
}

/**
 * Button Group Component
 * Based on common button group patterns from shadcnui-blocks
 * Use for grouped actions, tabs, segmented controls
 */
const UIButtonGroupRoot = React.forwardRef<HTMLDivElement, UIButtonGroupProps>(
  ({ className, orientation = 'horizontal', attached = true, children, ...props }, ref) => {
    const groupClasses = cn(
      "flex",
      orientation === 'horizontal' ? "flex-row" : "flex-col",
      attached && orientation === 'horizontal' && "[&>*:not(:first-child)]:rounded-l-none [&>*:not(:last-child)]:rounded-r-none [&>*:not(:first-child)]:-ml-px",
      attached && orientation === 'vertical' && "[&>*:not(:first-child)]:rounded-t-none [&>*:not(:last-child)]:rounded-b-none [&>*:not(:first-child)]:-mt-px",
      className
    )

    return (
      <UIBox
        ref={ref}
        className={groupClasses}
        role="group"
        {...props}
      >
        {children}
      </UIBox>
    )
  }
)

const UIButtonGroupItem = React.forwardRef<HTMLButtonElement, UIButtonGroupItemProps>(
  ({ className, variant = "outline", ...props }, ref) => {
    return (
      <UIButton
        variant={variant}
        className={cn(
          "relative focus:z-10 hover:z-10",
          className
        )}
        ref={ref}
        {...props}
      />
    )
  }
)

UIButtonGroupRoot.displayName = "UIButtonGroup"
UIButtonGroupItem.displayName = "UIButtonGroupItem"

export const UIButtonGroup = Object.assign(UIButtonGroupRoot, {
  Item: UIButtonGroupItem,
})